import React, { useState, useEffect, useRef, useMemo } from 'react';

// Icono Chevron local para evitar dependencias externas
const IconChevronDown = ({ className = "w-3 h-3 text-slate-400" }) => (
    <svg className={`${className} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

/**
 * MultiSelectDropdown - Selector múltiple con buscador y acordeón integrado.
 * 
 * Props:
 * - label: Texto descriptivo del campo (ej: "Estado de Cobranza")
 * - options: Array de strings con las opciones disponibles
 * - selectedValues: Array de strings con los valores seleccionados actualmente
 * - onChange: Función callback para actualizar la selección: (nuevosValores) => void
 * - isOpen: Booleano que indica si este desplegable está abierto
 * - onToggle: Función callback para alternar apertura: (boolean) => void
 */
const MultiSelectDropdown = ({ 
    label, 
    options = [], 
    selectedValues = [], 
    onChange, 
    isOpen = false, 
    onToggle 
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Cierre al hacer click fuera del componente
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                if (isOpen && onToggle) onToggle(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onToggle]);

    // Limpia la búsqueda automáticamente cuando se cierra el menú desplegable
    useEffect(() => {
        if (!isOpen) setSearchQuery('');
    }, [isOpen]);

    // Filtrado de opciones en tiempo real por búsqueda
    const filteredOptions = useMemo(() => {
        return (options || []).filter(opt => 
            String(opt || '').toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [options, searchQuery]);

    const handleToggle = (value) => {
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter(v => v !== value));
        } else {
            onChange([...selectedValues, value]);
        }
    };

    const hasSelection = selectedValues && selectedValues.length > 0;

    return (
        <div className="text-left w-full relative" ref={dropdownRef}>
            {/* Header del campo con contador de seleccionados */}
            <div className="flex items-center justify-between mb-1">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider">
                    {label}
                </label>
                {hasSelection && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-[#01032d] font-bold rounded-md">
                        {selectedValues.length} sel.
                    </span>
                )}
            </div>

            {/* Botón trigger del desplegable */}
            <button
                type="button"
                onClick={() => onToggle && onToggle(!isOpen)}
                className={`w-full border rounded-xl p-2.5 text-xs font-bold text-left flex justify-between items-center transition-all outline-none ${
                    hasSelection 
                        ? 'border-blue-900 bg-blue-50/20 text-[#01032d]' 
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
            >
                <span className="truncate max-w-[85%]">
                    {!hasSelection ? 'TODOS' : selectedValues.join(', ')}
                </span>
                <IconChevronDown className={`w-3 h-3 text-slate-400 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Contenedor desplegable (acordeón inline) */}
            {isOpen && (
                <div className="w-full mt-1.5 bg-slate-50 border border-slate-200/80 rounded-xl p-2.5 max-h-56 overflow-hidden flex flex-col animate-fade-in z-20">
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-xl p-2 text-[11px] mb-2 font-medium outline-none focus:border-blue-900 transition-colors"
                        autoFocus
                    />
                    
                    {/* Botones de acción rápida */}
                    <div className="flex justify-between mb-2 px-1 text-[10px] font-black uppercase text-[#01032d]">
                        <button 
                            type="button" 
                            onClick={() => onChange([])} 
                            className="hover:underline cursor-pointer"
                        >
                            Limpiar
                        </button>
                        <button 
                            type="button" 
                            onClick={() => onChange(options)} 
                            className="hover:underline cursor-pointer"
                        >
                            Todos
                        </button>
                    </div>

                    {/* Lista con checkboxes y scroll personalizado */}
                    <div className="overflow-y-auto space-y-1 flex-grow pr-1 custom-scrollbar">
                        {filteredOptions.length === 0 ? (
                            <p className="text-[11px] text-slate-400 italic text-center py-2">
                                Sin coincidencias
                            </p>
                        ) : (
                            filteredOptions.map(opt => {
                                const isChecked = selectedValues.includes(opt);
                                return (
                                    <label 
                                        key={opt} 
                                        className={`flex items-center gap-2.5 p-1.5 px-2 hover:bg-white rounded-lg cursor-pointer text-xs font-semibold transition-colors ${
                                            isChecked ? 'bg-blue-50/30 text-[#01032d]' : 'text-slate-600'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleToggle(opt)}
                                            className="w-4 h-4 rounded text-[#01032d] border-slate-300 focus:ring-slate-500 cursor-pointer"
                                        />
                                        <span className="truncate">{opt}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;